import React, { Fragment, useState, useEffect, useRef } from 'react-x';
import { createPortal } from 'react-dom-x';
import propTypes from 'prop-types-x';
import classNames from 'classnames';
import { useUpdateEffect } from '../hooks/useUpdateEffect.js';
import { useEventListener } from '../hooks/useEventListener.js';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { publish } from '../../utilities/events.js';
import { CORE_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { fireGTMTrackingEvent } from '../../utilities/fireGTMTrackingEvent.js';

export function Overlay(props) {

  const { 
    id, 
    children,
    classes, 
    closeButtonLabel, 
    brand,
    gtmComponentGroup, 
    gtmComponentName,
    gtmLabel,
    showCloseButton,
    theme,
    trackingOverride
  } = props;

  const [open, setOpen] = useState(false);
  const rootElementRef = useRef();
  const closeButtonRef = useRef();
  const childPropsRef = useRef({});
  const bodyElRef = useRef();
  const block = 'c-overlay';

  function openOverlay(e) {
    const overlayData = e.detail;
    if (!overlayData) return;

    if (overlayData.overlayId === id) {
      childPropsRef.current = overlayData;
      setOpen(true);
    } else {
      console.warn(`No Overlay component with id of "${overlayData.overlayId}" exists.`)
    }
  };

  function closeOverlay() {
    setOpen(false);
  };

  function navigateAway() {
    bodyElRef.current.style.overflow = 'auto';
    setOpen(false);
  };

  function resetElementFocus(e) {
    e.preventDefault();
    const tabBtnPressed = e.key === 'Tab' || e.keyCode === 9;
    if (tabBtnPressed) { 
      showCloseButton && closeButtonRef.current.focus();
    }
  };

  function trackEvent(eventName) {
    fireGTMTrackingEvent({
      name: eventName === 'open' ?
       CORE_GTM_TRACKING_VALUES.name.workflowEvent :
       CORE_GTM_TRACKING_VALUES.name.componentEvent,
      componentName: gtmComponentName,
      componentGroup: gtmComponentGroup,
      action: eventName === 'open' ? 
        CORE_GTM_TRACKING_VALUES.action.openOverlay : 
        CORE_GTM_TRACKING_VALUES.action.clickCta,
      label: eventName === 'open' ?
        gtmLabel || id :
        CORE_GTM_TRACKING_VALUES.label.close,
    },trackingOverride);
  };

  useEventListener('trigger-open-overlay', openOverlay);

  useEventListener('trigger-close-overlay-externally', closeOverlay);

  useEventListener('popstate', navigateAway);

  useEscapeKey(closeOverlay);

  useEffect(() => {
    bodyElRef.current = document.querySelector('body');

    if (open) {
      bodyElRef.current.style.overflow = 'hidden';
      showCloseButton && closeButtonRef.current.focus();
      publish('overlay-opened', { details: { id }}, window);
    } 
    else {
      bodyElRef.current.style.overflow = 'auto';
      publish('overlay-closed', { details: { id }}, window);
    }
  }, [open]);

  useUpdateEffect(() => {
    if (open) {
      trackEvent('open');
    } 
    else {
      trackEvent('close');
    }
  }, [open]);

  useEffect(() => {
    return () => {
      bodyElRef.current.style.overflow = 'auto';
    }
  });

  return (
    <Fragment>
      {
        open ?
          createPortal(
            <div 
            className={`${brand}-campaigns`}
            >
              <div 
              id={id}
              className={classNames({
                [`${block}`]: true,
                [theme]: true,
                [classes]: classes
              })}
              ref={rootElementRef}
              role='dialog' 
              aria-modal='true' 
              aria-live='assertive' 
              aria-labelledby={id}
              >
                {
                  showCloseButton &&
                    <button 
                    className={`${block}__close-btn`} 
                    ref={closeButtonRef} 
                    onClick={closeOverlay}
                    >
                      <span className={`${block}__close-btn-text alt-color cta`}>
                        { closeButtonLabel }
                      </span>
                    </button>
                }
                  { children }
                <div 
                aria-label='End of overlay content'
                tabIndex={0} 
                onKeyDown={resetElementFocus}
                style={{ opacity: 0, height: 0, width: 0 }}
                >
                </div>
              </div>
            </div>,
            document.body
          )
        :
        null
      }
    </Fragment>
  );
};

Overlay.propTypes = {
  id: propTypes.string.isRequired,
  children: propTypes.oneOfType([
    propTypes.arrayOf(propTypes.node),
    propTypes.node
  ]).isRequired,
  classes:propTypes.string,
  closeButtonLabel: propTypes.string,
  brand: propTypes.string,
  gtmComponentGroup: propTypes.string,
  gtmComponentName: propTypes.string,
  gtmLabel: propTypes.string,
  showCloseButton: propTypes.bool,
  theme: propTypes.string
};

Overlay.defaultProps = {
  children: <div>Overlay content</div>,
  closeButtonLabel: 'CLOSE',
  brand: 'lexus',
  gtmComponentName: CORE_GTM_TRACKING_VALUES.componentName.overlay,
  gtmComponentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.campaigns,
  showCloseButton: true,
  theme: 'light-theme-lighter'
};